import * as ko from 'knockout';
import { FirebaseError, getApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  sendPasswordResetEmail,
  TotpMultiFactorGenerator,
} from 'firebase/auth';
import {
  initializeUI,
  signInWithProvider,
  signInWithEmailAndPassword,
  signInWithMultiFactorAssertion,
  getTranslation,
  FirebaseUI,
} from "@firebase-oss/ui-core";
import { ERROR_CODE_MAP } from "@firebase-oss/ui-translations";
import i18n from '../i18n';
import { session } from "../session";

let template =
  require("raw-loader!../../templates/components/firebase_login.html").default;
const googleLogo = require("@firebase-oss/ui-core/brands/google/logo.svg");
const microsoftLogo = require("@firebase-oss/ui-core/brands/microsoft/logo.svg");

const TOTP_CODE_LENGTH = 6;

interface FirebaseLoginParams {
  tenantId: string | ko.Observable<string>;
  email?: string | ko.Observable<string>;
}

interface ProviderConfig {
  id: string;
  label: string;
  shortLabel: string;
  logo: any;
  provider: any;
  bgColor: string;
  fullClass: string;
}

class FirebaseLoginViewModel {
  private unsubscribe: () => void;
  error = ko.observable("");
  successMessage = ko.observable("");
  providers = ko.observableArray<ProviderConfig>([]);
  showEmailPassword = ko.observable(false);
  showTotpInput = ko.observable(false);
  totpCode = ko.observable('');
  email: string;
  password = ko.observable("");
  ui: FirebaseUI;
  passwordPlaceholder: string;
  signInLabel: string;

  constructor(params: FirebaseLoginParams) {
    const tenantId = ko.unwrap(params.tenantId);
    const email = ko.unwrap(params.email);

    const auth = getAuth(session.firebaseApp || getApp());
    auth.tenantId = tenantId || null;

    const uiStore = initializeUI({ app: auth.app, auth });

    this.ui = uiStore.get();
    this.passwordPlaceholder = i18n.t("Password")();
    this.signInLabel = i18n.t("Sign in")();

    this.totpCode.subscribe((value) => {
      const sanitized = value.replace(/\D/g, '').slice(0, TOTP_CODE_LENGTH);
      if (sanitized !== value) {
        this.totpCode(sanitized);
      }
    });

    // uiStore.subscribe returns a function that removes the listener
    this.unsubscribe = uiStore.subscribe((ui) => {
      this.ui = ui;
      if (ui.multiFactorResolver) {
        this.showTotpInput(true);
      }
    });

    if (tenantId) {
      this.showEmailPassword(true);
      this.email = email || "";
    } else {
      this.providers([
        {
          id: "google.com",
          label: i18n.t("Sign in with Google")(),
          shortLabel: "Google",
          logo: googleLogo,
          provider: new GoogleAuthProvider(),
          bgColor: "#ffffff",
          fullClass: "firebaseui-idp-google",
        },
        {
          id: "microsoft.com",
          label: i18n.t("Sign in with Microsoft")(),
          shortLabel: "Microsoft",
          logo: microsoftLogo,
          provider: new OAuthProvider("microsoft.com"),
          bgColor: "#ffffff",
          fullClass: "firebaseui-idp-microsoft",
        },
      ]);
    }
  }

  dispose() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }

  async signIn(providerConfig: ProviderConfig) {
    this.error("");
    try {
      await signInWithProvider(this.ui, providerConfig.provider);
    } catch (e) {
      this.handleError(e);
    }
  }

  async signInEmailPassword() {
    this.error("");
    try {
      await signInWithEmailAndPassword(this.ui, this.email, this.password());
    } catch (e) {
      if (this.ui.multiFactorResolver) {
        return;
      }
      this.handleError(e);
    }
  }

  async verifyTotpCode() {
    this.error('');
    if (this.totpCode().length !== TOTP_CODE_LENGTH) {
      return;
    }
    try {
      const totpHint = this.ui.multiFactorResolver!.hints.find(
        (h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID
      )!;
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(totpHint.uid, this.totpCode());
      await signInWithMultiFactorAssertion(this.ui, assertion);
    } catch (e) {
      this.handleError(e);
    }
  }

  private handleError(e: FirebaseError) {
    this.ui.setState("idle");
    // Suppress UI error message for manual cancellations
    if (
      e.code === "auth/popup-closed-by-user" ||
      e.code === "auth/cancelled-popup-request"
    ) {
      return;
    }
    if (e.code && ERROR_CODE_MAP && (ERROR_CODE_MAP as any)[e.code]) {
      this.error(
        getTranslation(this.ui, "errors", (ERROR_CODE_MAP as any)[e.code]),
      );
    } else {
      this.error(e.message || "An error occurred");
    }
  }

  async forgotPassword() {
      this.error(""); 
      this.successMessage(""); 
      const auth = getAuth(session.firebaseApp || getApp());

      if (!this.email) {
          this.error(i18n.t("Please enter your email address to reset your password.")());
          return;
      }

      try {
          await sendPasswordResetEmail(auth, this.email);
          this.successMessage(i18n.t("Recovery email successfully sent.")());
      } catch (e) {
          console.error("Error sending password reset email:", e);
          this.handleError(e);
      }
  }

  goBack() {
    window.location.reload();
  }
}

ko.components.register("firebase-login", {
  viewModel: FirebaseLoginViewModel,
  template: template,
});
